/**
 * Turn an HTTP 402 from the entitlement guard into copy a person can act on.
 *
 * The server answers a blocked premium feature with 402 and a body of
 * `{ message, data: { upgrade_required: true, feature } }`. Every client
 * currently drops that on the floor: the share dialog says "Couldn't create a
 * share link. Please try again.", which words a permanent refusal as a
 * transient one and sends the customer round the same loop forever.
 *
 * Two things this fixes beyond surfacing the server string:
 *
 *  - The server message is one generic sentence for all five gates. Naming the
 *    feature is the difference between "something is premium" and "safe zones
 *    are premium", and only the second tells the customer what they just lost.
 *  - Changing the plan is OWNER-only. Telling a member to upgrade sends them to
 *    a Subscribe button that returns 403, so `canUpgrade` decides between an
 *    instruction and a referral. Same reasoning as the circle-owner gating on
 *    the billing entry points.
 *
 * `.mjs` matches `circleOwner.mjs`, the existing helper module here. An earlier
 * version of this comment justified it by claiming node could not import a
 * `.js` from `node --test` without a `"type": "module"`. That is wrong on this
 * toolchain - node auto-detects and imports `useApiError.js` fine, with only a
 * MODULE_TYPELESS_PACKAGE_JSON warning. The extension is a house-style choice
 * for alias-free shared helpers, not a technical requirement.
 *
 * NOTE: enforcement is off in production (`ENTITLEMENTS_ENFORCED=false`), so no
 * live customer can reach a 402 today. This is the client half being made ready
 * ahead of the switch, not a fix to anything users are hitting now.
 */

/**
 * What the customer actually lost, per gate key the server can send.
 *
 * Deliberately phrased as a plain statement of fact rather than a sales line -
 * the sentence that follows it is the one asking for money.
 */
export const FEATURE_LINES = {
    share_live: "Sharing a live location link is a Premium feature.",
    max_zones: "You have used every place your plan includes.",
    max_places: "You have used every saved location your plan includes.",
    alert_speed: "Speed alerts are a Premium feature.",
    alert_noshow: "No-show alerts are a Premium feature.",
    mileage_log: "The Mileage Log is a Premium feature.",
};

/**
 * Only the gates that can actually answer 402, checked against the server's
 * `upgradeResponse()` call sites - the four entitlement ones, plus `mileage_log`
 * from api #1330, which closed a paywall that had been enforced in the clients
 * alone.
 *
 * Three catalogue keys are deliberately absent. `history_days` clamps a range
 * and returns 200, so it never refuses a request. `weekly_summary` has a
 * catalogue row but no endpoint gates on it. And `driver_insights` is not a
 * catalogue key at all - it is a boolean method on the guard with no
 * feature_catalog row - so a line for it would be copy for a refusal that
 * cannot be produced. An earlier version of this map carried lines for
 * `history_days` and `driver_insights`, which was dead copy that made the map
 * look more complete than it was.
 *
 * `max_places` IS reachable server-side, on `POST companies/locations/{company}`
 * - but this client never calls that endpoint. Kept so the map matches what the
 * server can send rather than what this client happens to reach today.
 *
 * `max_zones` is worded as "place", matching the Flutter client and matching the
 * screen it appears on: PlaceDialog is titled "Save a place" / "Edit place" and
 * validates with "Please name this place." It is the cap behind
 * `monitoring/geofence-templates`, which neither Go client calls a safe zone.
 * Naming the feature is the point of this map, so it has to name the thing the
 * customer just tapped - and the two clients have to say the same sentence for
 * the same server key.
 */

/**
 * Read an axios error as an upgrade refusal, or return null if it is not one.
 *
 * @param {unknown} error         the rejected axios error
 * @param {object}  [options]
 * @param {boolean} [options.canUpgrade=true] whether THIS user may change the plan
 * @returns {{feature: string|null, message: string, canUpgrade: boolean}|null}
 */
export function upgradeInfo(error, { canUpgrade = true } = {}) {
    const response = error?.response;

    // Keyed on the status, not on the body flag. 402 is reserved for exactly
    // this in the API - EntitlementGuard::upgradeResponse is its only source -
    // and a body-only check would go blind the moment a gateway or an error
    // page reshapes the payload. The flag is corroboration, not the signal.
    if (response?.status !== 402) return null;

    const feature = response?.data?.data?.feature ?? null;
    const serverMessage = response?.data?.message;
    const line =
        // hasOwn, not a bare lookup: a `feature` of "constructor" or "toString"
        // would otherwise walk the prototype and render
        // "function Object() { [native code] } Upgrade your circle plan...".
        (typeof feature === "string" && Object.hasOwn(FEATURE_LINES, feature)
            ? FEATURE_LINES[feature]
            : null) ??
        // An unknown key means a NEW gate shipped on the server before this map
        // learned about it. Falling back to the server's own sentence keeps that
        // case honest instead of inventing a reason.
        (typeof serverMessage === "string" && serverMessage.trim() !== ""
            ? serverMessage
            : null) ??
        "This is a Premium feature.";

    const ask = canUpgrade
        ? "Upgrade your circle plan to unlock it."
        : "Ask your circle owner to upgrade.";

    return { feature, message: `${line} ${ask}`, canUpgrade };
}
